import React from "react";
import Reveal from "./Reveal";

/**
 * This Is For You If...
 *
 * Audience identification section. Four columns reframing the
 * archetypes that Authentic Momentum was actually built for. The
 * goal is recognition: the visitor reads one card and feels seen.
 * If they don't, they self-exit cleanly without being shamed for it.
 *
 * Editorial cards on the standard charcoal panel pattern so the
 * descriptive copy never sits on raw black.
 */

const ARCHETYPES = [
  {
    id: "founders",
    name: "Founders",
    line:
      "You are building something real and the strategy is right. The push is right. The thing that is missing is the quieter signal underneath the next decision.",
  },
  {
    id: "builders",
    name: "Builders",
    line:
      "You make things. You ship. And lately, the work is starting to ask more of you than it used to. Not because it is harder. Because you have grown past the frame.",
  },
  {
    id: "creatives",
    name: "Creatives",
    line:
      "You hear it before you can name it. The thing that wants to come through has been waiting on you to slow down enough to listen on purpose.",
  },
  {
    id: "entrepreneurs",
    name: "Entrepreneurs",
    line:
      "You have proven you can move. The question now is which move actually fits where you are, in the timing that is honest for you and the business.",
  },
];

// Qualifier rows. Sit beneath the archetype grid so the visitor
// can self-locate AND self-exit cleanly. The brand stays specific
// instead of trying to be useful to everyone.
const FOR_YOU = [
  "You have ideas, but no clean structure.",
  "You know you are meant for more, but do not know what to build first.",
  "You are tired of carrying potential without a clear pathway.",
  "You need language for what you do.",
  "You need help turning your gift into an offer, system, brand, or next move.",
  "You have relationships and opportunities, but no activation strategy.",
  "You are ready for clarity, but not more empty motivation.",
  "You want grounded movement, not just inspiration.",
];

const NOT_FOR_YOU = [
  "You only want inspiration with no action.",
  "You are not willing to participate in the process.",
  "You want someone else to carry the entire vision for you.",
  "You are not ready to make decisions or move with structure.",
];

const ThisIsForYouIf = () => (
  <section
    id="this-is-for-you-if"
    data-testid="this-is-for-you-if-section"
    className="relative py-24 md:py-32 border-t border-gold/10"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
          Audience
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight tracking-tight font-light text-center max-w-3xl mx-auto">
          This is for you{" "}
          <span className="italic gold-gradient-text">if...</span>
        </h2>
      </Reveal>

      <div
        data-testid="this-is-for-you-grid"
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6"
      >
        {ARCHETYPES.map((a, i) => (
          <Reveal key={a.id} delay={120 + i * 90}>
            <div
              data-testid={`this-is-for-you-card-${a.id}`}
              className="bg-white/5 border-t border-gold/20 p-7 md:p-8 rounded-lg border border-gold/10 h-full"
            >
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85">
                For
              </p>
              <h3 className="mt-2 font-serif text-2xl md:text-[1.65rem] text-themed font-light">
                {a.name}
              </h3>
              <p className="mt-4 text-white/85 text-[15.5px] md:text-[16px] leading-relaxed">
                {a.line}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Qualifier rows. The first list invites self-recognition. The second
          list protects the brand from over-broadening by giving honest
          exits to visitors who want a different kind of experience. */}
      <div
        data-testid="this-is-for-you-qualifier"
        className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6"
      >
        <Reveal delay={120}>
          <div className="bg-white/5 border-t border-gold/20 p-7 md:p-9 rounded-lg border border-gold/10 h-full">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">
              This is for you if
            </p>
            <ul data-testid="this-is-for-you-list" className="space-y-3">
              {FOR_YOU.map((line, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-white/85 text-[15px] md:text-[15.5px] leading-relaxed"
                >
                  <span className="text-gold/80 shrink-0">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className="bg-white/[0.03] border-t border-gold/10 p-7 md:p-9 rounded-lg border border-gold/[0.08] h-full">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/65 mb-4">
              This may not be for you if
            </p>
            <ul data-testid="this-is-not-for-you-list" className="space-y-3">
              {NOT_FOR_YOU.map((line, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-white/65 text-[15px] md:text-[15.5px] leading-relaxed"
                >
                  <span className="text-gold/40 shrink-0">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default ThisIsForYouIf;
